export type SettingsListItem = { id: string };

export type SettingsListColumn<T extends SettingsListItem = SettingsListItem> =
  {
    // Column identity: the v-for key AND the `cell-<key>` slot-name suffix.
    key: string;
    // Head-row text. Written lowercase in Italian - the head capitalizes via CSS.
    label: string;
    // Bootstrap grid width, e.g. "col-4". The head cell and the body cell are rendered
    // from this same string, so the two rows cannot drift apart.
    col: string;
    // Default cell text. Omit when a `cell-<key>` slot renders the cell instead.
    value?: (item: T) => string;
    // Native tooltip on the cell, for truncated text.
    title?: (item: T) => string | undefined;
    // Extra classes on the body cell, e.g. "text-truncate text-muted".
    cellClass?: string;
    // Extra classes on the head cell only, e.g. "text-right".
    headClass?: string;
  };

export type SettingsListActionVariant = "default" | "danger";

export type SettingsListAction<T extends SettingsListItem = SettingsListItem> =
  {
    // Emitted back as `action({ key, item })`.
    key: string;
    // What the owning list does when the action fires.
    run: (item: T) => void;
    // <v-icon> body; `iconV2` selects the newer icon set.
    icon: string;
    iconV2?: boolean;
    // Native tooltip / accessible label (Italian).
    title: string;
    // "danger" adds .icon-btn-danger.
    variant?: SettingsListActionVariant;
    // Defaults: always visible, never disabled.
    visible?: (item: T) => boolean;
    disabled?: (item: T) => boolean;
  };

export type SettingsListActionEvent<
  T extends SettingsListItem = SettingsListItem
> = {
  key: string;
  item: T;
};

// Single source of truth for the per-column slot name, shared by the container (which
// forwards the slot) and the row (which renders the outlet).
export const cellSlotName = (key: string): string => `cell-${key}`;
